import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import SaveIcon from "@mui/icons-material/FileDownloadOutlined";
import PublishIcon from "@mui/icons-material/LanguageRounded";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { convertISODate } from "@entrilia/shared/utilities/dateUtils";
import adminApi from "../../../../api/adminApi";
import type { DocumentCollectionDetails } from "../../../../api/types/documentCollectionTypes";
import useClientContext from "../../../../context/useClientContext";
import UnpublishIcon from "@entrilia/shared/icons/UnpublishIcon";
import { pageRoutes } from "../../../../routes";
import DocumentCollectionPortalStatusTag from "../DocumentCollectionPortalStatusTag";
import DocumentCollectionStatusTag from "../DocumentCollectionStatusTag";
import DeliveryProgress from "./DeliveryProgress";
import DocumentCollectionContext from "./DocumentCollectionContext";

interface Props {
  documentCollectionDetails: DocumentCollectionDetails;
}

const getDownloadUrlForDocumentCollection = withErrorHandling(adminApi.getDownloadUrlForDocumentCollection);

const DocumentCollectionHeader = ({ documentCollectionDetails }: Props) => {
  const navigate = useNavigate();
  const { clientCode } = useClientContext();
  const { hasPermissionsToManage, investorPortalEnabled, onChangeDocumentCollectionStatus } =
    useDefinedContext(DocumentCollectionContext);

  const handleBackClick = () => {
    navigate(`/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.documentCollections}`, {
      state: { documentCollectionId: documentCollectionDetails.id },
    });
  };

  const handleDownloadAllClick = async () => {
    const [url, error] = await getDownloadUrlForDocumentCollection(documentCollectionDetails.id, clientCode);
    if (error) {
      logError(error, "[DocumentCollectionHeader] handleDownloadAllClick");
      return;
    }

    await downloadFileFromUrl(url);
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Box sx={{ ml: 1, display: "flex", alignItems: "flex-start", gap: 2 }}>
        <IconButton onClick={handleBackClick}>
          <ArrowBackRoundedIcon />
        </IconButton>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <Typography variant="h6">{documentCollectionDetails.name}</Typography>
            {investorPortalEnabled && (
              <DocumentCollectionPortalStatusTag portalPublishStatus={documentCollectionDetails.portalPublishStatus} />
            )}
            <DocumentCollectionStatusTag status={documentCollectionDetails.status} />
          </Stack>

          <Stack direction="row" spacing={1}>
            <Typography variant="caption" color="textSecondary">
              Document Collection
            </Typography>
            {documentCollectionDetails.no !== undefined && (
              <>
                <Typography variant="caption">&bull;</Typography>
                <Typography variant="caption" color="textSecondary">
                  ID:
                </Typography>
                <Typography variant="caption">{documentCollectionDetails.no.toLocaleString()}</Typography>
              </>
            )}
            <Typography variant="caption">&bull;</Typography>
            <Typography variant="caption" color="textSecondary">
              Fund:
            </Typography>
            <Typography variant="caption">
              {documentCollectionDetails.funds.length > 1
                ? "Multiple"
                : (documentCollectionDetails.funds[0]?.name ?? "")}
            </Typography>
            <Typography variant="caption">&bull;</Typography>
            <Typography variant="caption" color="textSecondary">
              Level:
            </Typography>
            <Typography variant="caption">{documentCollectionDetails.messageLevel}</Typography>
            <Typography variant="caption">&bull;</Typography>
            <Typography variant="caption" color="textSecondary">
              Publish Date:
            </Typography>
            <Typography variant="caption">{convertISODate(documentCollectionDetails.publishDate)}</Typography>
          </Stack>
        </Stack>
      </Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <DeliveryProgress
          documentsCount={documentCollectionDetails.documents.length}
          deliveryProgress={documentCollectionDetails.deliveryProgress}
        />
        <Button color="secondary" variant="outlined" onClick={handleDownloadAllClick} startIcon={<SaveIcon />}>
          All Documents
        </Button>

        {hasPermissionsToManage && documentCollectionDetails.availableOperations.includes("MakeLive") && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<PublishIcon />}
            onClick={() => onChangeDocumentCollectionStatus("MakeLive")}
          >
            Publish to Live
          </Button>
        )}

        {hasPermissionsToManage && documentCollectionDetails.availableOperations.includes("MakePreview") && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<UnpublishIcon />}
            onClick={() => onChangeDocumentCollectionStatus("MakePreview")}
          >
            Unpublish to Preview
          </Button>
        )}

        {hasPermissionsToManage && documentCollectionDetails.availableOperations.includes("Revoke") && (
          <ActionsMenuButton
            items={[
              {
                icon: <DeleteIcon color="error" />,
                label: "Delete",
                onClick: () => onChangeDocumentCollectionStatus("Revoke"),
              },
            ]}
          />
        )}
      </Stack>
    </Box>
  );
};

export default DocumentCollectionHeader;
